import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
// import UserNotRegisteredError from '@/components/UserNotRegisteredError'

// Layouts
import MainLayout from './components/layout/MainLayout';
import DashboardLayout from './components/layout/DashboardLayout';

// Public pages
import Home from './pages/Home';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import AuthPage from './pages/AuthPage';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import JobDetail from './pages/JobDetail';
import ForColleges from './pages/ForColleges';
import SignOTP from './pages/SignOTP';

// Student dashboard pages
import Dashboard from './pages/Dashboard';
import CoursePlayer from './pages/CoursePlayer';
import Assignments from './components/AssignmentsSection';
import Quizzes from './pages/Quizzes';
import Certificates from './pages/Certificates';
import Wishlist from './pages/Wishlist';
import Profile from './pages/Profile';

// Instructor
import InstructorDashboard from './pages/InstructorDashboard';
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import CourseDashboard from "./pages/CourseDashboard";
import ActivityTracker from './components/ActivityTracker';

// /my-courses and /resume-course/:course_url were merged into the dashboard;
// keep the old URLs working for bookmarks and notification links.
const ResumeCourseRedirect = () => {
  const { course_url } = useParams();
  return <Navigate to={`/dashboard/${course_url}`} replace />;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <Routes>
      {/* Main Layout (with navbar + footer) */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/course/:id" element={<CourseDetail />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:slug" element={<JobDetail />} />
        <Route path="/for-colleges" element={<ForColleges />} />
        <Route path="/sign-otp" element={<SignOTP/>}/>
        <Route path="/login" element={<Login/>} />
        <Route path="/forgot-password" element={<ForgotPassword/>} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/resume-course/:course_url" element={<ResumeCourseRedirect />} />
        <Route path="/dashboard/:course_url" element={<CourseDashboard />} />


      </Route>

      {/* Student Dashboard Layout */}
      <Route element={<DashboardLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-courses" element={<Navigate to="/dashboard" replace />} />
        <Route path="/assignments" element={<Assignments />} />
        <Route path="/quizzes" element={<Quizzes />} />
        <Route path="/certificates" element={<Certificates />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/profile" element={<Profile />} />
      </Route>

      {/* Full-screen pages */}
      <Route path="/player/:id" element={<CoursePlayer />} />
      <Route path="/instructor" element={<InstructorDashboard />} />

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ActivityTracker />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App